import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useModalClose from 'hooks/useModalClose'
import { Button, CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { IKitchenMenuCell } from 'types/kitchen/kitchenMenu.interface'
import { IKitchenSite } from 'types/kitchen/kitchenSite.interface'
import { kitchenDemandFor } from '../kitchenData'
import { MenuCellRow, MenuCellSelectWrap, ModalTableScroll } from '../Kitchen.styled'

interface ITagRow {
  id: string
  tag: string
  demand: number
  cur: number
  purchased: boolean
  short: boolean
  cookableCount: number | null
}

interface IProps {
  cell: IKitchenMenuCell
  kitchen: IKitchenSite
  tagSets: string[]
  dishes: IKitchenDish[]
  assign: { [key: string]: number }
  setAssign: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
  status: { [key: string]: string }
  pastMenu: { [window: string]: number[] }
  cookable: (dish: IKitchenDish) => number | null
  closeModal: () => void
  onPublish: () => void
}

export const MenuCellModal = ({
  cell,
  kitchen,
  tagSets,
  dishes,
  assign,
  setAssign,
  status,
  pastMenu,
  cookable,
  closeModal,
  onPublish,
}: IProps) => {
  useModalClose(closeModal)

  const { day, window: w } = cell
  const past = day.kind === 'past'
  const keyOf = (t: string) => `${kitchen.id}-${day.d}-${w}-${t}`
  const allCovered = tagSets.every((t) => assign[keyOf(t)])
  const isPublished = status[`${kitchen.id}-${day.d}-${w}`] === 'Published'

  const dish = (id: number) => dishes.find((d) => d.id === id)

  const certainty = (kind: string) =>
    kind === 'past' ? 'actual' : kind === 'today' || kind === 'tomorrow' ? 'forecast (T-1)' : 'estimate'

  if (past) {
    const pastRows = (pastMenu[w] || []).map((id) => ({
      id,
      name: dish(id)?.name || '—',
      served: w === 'Lunch' ? '118' : w === 'Breakfast' ? '131' : '102',
    }))
    const pastColumns: IHonestTableColumn<typeof pastRows[0]>[] = [
      { id: 'name', header: 'Dish', cell: (r) => <BodyText weight='600'>{r.name}</BodyText> },
      { id: 'served', header: 'Served', cell: (r) => <BodyText>{r.served}</BodyText> },
      {
        id: 'consumption',
        header: 'Consumption',
        cell: () => (
          <OtherText style={{ color: colors.orange, cursor: 'pointer', textDecoration: 'underline' }}>
            Posted issues →
          </OtherText>
        ),
      },
    ]
    return (
      <Modal black width='560px'>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center>
            <TitleText weight='bold'>
              {w} — {day.label} · {kitchen.name}
            </TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <ModalTableScroll>
            <HonestTable columns={pastColumns} data={pastRows} keyField='id' />
          </ModalTableScroll>
          <OtherText grey>History is read-only — the menu as served, linked to actual ledger consumption.</OtherText>
          <Flex gap='0.5rem' justcenter width='100%'>
            <Button onClick={closeModal}>Close</Button>
          </Flex>
        </Flex>
      </Modal>
    )
  }

  const rows: ITagRow[] = tagSets.map((t) => {
    const cur = assign[keyOf(t)]
    const demand = kitchenDemandFor(w, t)
    const c = cur ? cookable(dish(cur)!) : null
    const purchased = !!(cur && c === null)
    const short = !!(cur && !purchased && c !== null && c < demand)
    return { id: t, tag: t, demand, cur, purchased, short, cookableCount: c }
  })

  const renderFeasibility = (r: ITagRow) => {
    if (!r.cur) {
      return (
        <StatusPill status='accent'>Unassigned</StatusPill>
      )
    }
    if (r.purchased) {
      return (
        <StatusPill status='success'>Purchased / contractor</StatusPill>
      )
    }
    if (!r.short) {
      return (
        <StatusPill status='success'>{`${r.cookableCount} cookable`}</StatusPill>
      )
    }
    return (
      <Flex between center width='100%'>
        <StatusPill status='danger'>{`${r.cookableCount} vs ${r.demand}`}</StatusPill>
        <Button orangeBorder>Draft PR</Button>
      </Flex>
    )
  }

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>
            {w} — {day.label} · {kitchen.name}
          </TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <OtherText grey>
          Demand certainty:{' '}
          <BodyText weight='700' style={{ display: 'inline', color: day.kind === 'future' ? colors.orange : undefined }}>
            {certainty(day.kind)}
          </BodyText>
          {day.kind === 'future' && ' — quantities firm up on the rolling horizon (T-1)'}
        </OtherText>

        <Flex column gap='0.75rem' width='100%'>
          {rows.map((r) => {
            const legal = dishes.filter((d) => d.active && d.tags.includes(r.tag))
            const options = [
              { label: '— assign dish —', value: null },
              ...legal.map((d) => ({ label: d.name, value: d.id })),
            ]
            return (
              <MenuCellRow key={r.id}>
                <Flex between center width='100%' wrap gap='0.5rem'>
                  <StatusPill status='accent'>{r.tag}</StatusPill>
                  <OtherText grey>
                    Demand ({certainty(day.kind)}):{' '}
                    <BodyText weight='600' style={{ display: 'inline' }}>
                      {day.kind === 'future' ? '≈' : ''}
                      {r.demand}
                    </BodyText>
                  </OtherText>
                </Flex>
                <MenuCellSelectWrap>
                  <HonestSelectInput
                    key={`${r.tag}-${r.cur || 'none'}`}
                    width='100%'
                    labelText='Dish'
                    name={`dish-${r.tag}`}
                    options={options}
                    defaultValue={r.cur || null}
                    onChange={(v: any) =>
                      setAssign((a) => ({
                        ...a,
                        [keyOf(r.tag)]: v?.value || undefined,
                      }))
                    }
                    nomarg
                  />
                </MenuCellSelectWrap>
                <Flex column gap='0.25rem' width='100%'>
                  <OtherText grey>Feasibility (current stock)</OtherText>
                  {renderFeasibility(r)}
                </Flex>
              </MenuCellRow>
            )
          })}
        </Flex>

        <Flex gap='0.5rem' justcenter width='100%'>
          <Button onClick={closeModal}>Close</Button>
          <Button orange disabled={!allCovered || isPublished} onClick={onPublish}>
            {isPublished ? 'Published' : 'Publish'}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
